import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Alert, Form, Button, Row, Col, Spinner, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getExtractionFieldsByRulesetType } from '../services/api/rulesetApi';
import { resolveDiscrepancies, exportExtractionJob, retryExtractionJob, markAsUnprocessable, upgradeLineItems } from '../services/api/extractionApi';
import EditableField from './EditableField';
import { validateInput } from '../utils/sanitizer';
import { EXTRACTION_JOB_STATUS } from '../constants/statusConstants';
import { RESOLUTION_STATUS } from '../constants/resolutionConstants';
import { useLineItemManagement } from '../hooks/useLineItemManagement';

// Helper: determines if the job is in an editable status
const isEditableStatus = (statusId) =>
  statusId === EXTRACTION_JOB_STATUS.NEEDS_REVIEW ||
  statusId === EXTRACTION_JOB_STATUS.COMPLETED ||
  statusId === EXTRACTION_JOB_STATUS.READY_FOR_EXPORT;

const ExtractionDetailsView = ({ job, onJobUpdate, showPdfViewer = false, pdfUrl, loadingPdf, comparison = null, questionCode = '', errorCode = '', onEditableResultsChange = null }) => {
  const navigate = useNavigate();
  const [configuredFields, setConfiguredFields] = useState([]);
  const [editableResults, setEditableResults] = useState({});
  const [originalResults, setOriginalResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [exporting, setExporting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [reviewComments, setReviewComments] = useState('');
  const [showUnprocessableModal, setShowUnprocessableModal] = useState(false);
  const [unprocessableReason, setUnprocessableReason] = useState('');
  const [retrying, setRetrying] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    // Try to get ruleset type ID from different possible locations
    const rulesetTypeId = job?.extractionRuleId;
    
    if (rulesetTypeId) {
      loadConfiguredFields(rulesetTypeId);
    } else {
      // No configuration available, stop loading and show fallback
      setLoading(false);
      setConfiguredFields([]);
    }
  }, [job]);

  useEffect(() => {
    if (job?.finalResults) {
      setEditableResults(prev => {
        // Always update if job status is COMPLETED or READY_FOR_EXPORT (after resolving discrepancies)
        // or if we don't have editable results yet (initial load)
        if (isEditableStatus(job.statusId) || Object.keys(prev).length === 0) {
          return { ...job.finalResults };
        }
        return prev;
      });
      // Always update original results to show AI-extracted values
      setOriginalResults({ ...(job.extractionResults || job.finalResults) });
    }
  }, [job?.finalResults, job?.extractionResults, job?.statusId]);

  // Notify parent of editable results changes so the top-level save button stays in sync
  useEffect(() => {
    if (onEditableResultsChange) {
      onEditableResultsChange(editableResults);
    }
  }, [editableResults, onEditableResultsChange]);

  // Reset review comments when job changes
  useEffect(() => {
    setReviewComments('');
  }, [job?.id]);

  // Compute detail fields for the hook
  const detailFields = configuredFields.filter(field => field.fieldType === 1 || field.fieldType === 'Detail');

  // Use the line item management hook
  const { handleAddLineItem, handleRemoveLineItem, deletedOriginalIndices, resetDeletedIndices, lineItemChanges, clearLineItemChanges } = useLineItemManagement({
    detailFields,
    editableResults,
    setEditableResults,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    validationErrors,
    setValidationErrors
  });

  const loadConfiguredFields = async (rulesetTypeId) => {
    try {
      setLoading(true);
      const response = await getExtractionFieldsByRulesetType(rulesetTypeId);
      setConfiguredFields(response.data || []);
    } catch (err) {
      console.error('Error loading configured fields:', err);
      // Don't show error, just fall back to showing extracted data
      setConfiguredFields([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldKey, value, hasError = false) => {
    if (!hasError) {
      const currentValue = normalizeValue(editableResults[fieldKey]);
      const newValue = normalizeValue(value);
      
      if (currentValue !== newValue) {
        setEditableResults(prev => ({
          ...prev,
          [fieldKey]: value
        }));
      }
    }
    
    // Clear validation error for this field when value is valid
    if (validationErrors[fieldKey] && !hasError) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };
  
  const handleFieldValidationError = (fieldKey, hasError, errorMessage) => {
    if (hasError) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldKey]: errorMessage
      }));
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  const handleLineItemChange = (lineIndex, fieldKey, value, hasError = false) => {
    const fullFieldKey = `LineItems[${lineIndex}].${fieldKey}`;
    
    if (!hasError) {
      const currentValue = normalizeValue(editableResults.LineItems?.[lineIndex]?.[fieldKey]);
      const newValue = normalizeValue(value);
      
      if (currentValue !== newValue) {
        setEditableResults(prev => {
          const lineItems = [...(prev.LineItems || [])];
          if (!lineItems[lineIndex]) {
            lineItems[lineIndex] = {};
          }
          lineItems[lineIndex][fieldKey] = value;
          return {
            ...prev,
            LineItems: lineItems
          };
        });
      }
    }
    
    // Clear validation error for this field when value is valid
    if (validationErrors[fullFieldKey] && !hasError) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fullFieldKey];
        return newErrors;
      });
    }
  };
  
  const handleLineItemValidationError = (lineIndex, fieldKey, hasError, errorMessage) => {
    const fullFieldKey = `LineItems[${lineIndex}].${fieldKey}`;
    
    if (hasError) {
      setValidationErrors(prev => ({
        ...prev,
        [fullFieldKey]: errorMessage
      }));
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fullFieldKey];
        return newErrors;
      });
    }
  };

  const handleSaveChanges = async () => {
    // Check for any client-side validation errors before proceeding
    const hasValidationErrors = Object.keys(validationErrors).length > 0;
    if (hasValidationErrors) {
      setError('Please fix validation errors before saving');
      setTimeout(() => setError(''), 5000);
      return;
    }
    
    // Pre-populate with existing comments if they exist
    setReviewComments(job?.reviewComments || '');
    
    // Show comments modal
    setShowCommentsModal(true);
  };

  const performSave = async (comments = '') => {    
    try {
      setSaving(true);
      setShowCommentsModal(false);
      
      setValidationErrors({});
      
      // Build payload with header fields in resolutions and LineItems as a separate top-level array
      const resolutions = {};
      
      // Header fields only in resolutions (not line items)
      Object.keys(editableResults).forEach(key => {
        if (key !== 'LineItems') {
          resolutions[key] = editableResults[key];
        }
      });
      
      const payload = { resolutions };
      
      // Line items as a separate top-level array (not as LineItems[n].field resolution keys)
      // Each existing item includes _lineItemId; new items won't have it
      // Coerce all values to strings to preserve numeric precision (e.g., "2.00" not 2)
      if (editableResults.LineItems) {
        payload.LineItems = editableResults.LineItems.map(item => {
          const stringified = {};
          Object.entries(item).forEach(([key, value]) => {
            stringified[key] = value != null ? String(value) : '';
          });
          return stringified;
        });
      }
      
      if (comments.trim()) {
        payload.reviewComments = comments.trim();
      }
      // Include questionCode and errorCode from parent props
      payload.questionCode = questionCode;
      payload.errorCode = errorCode;
      
      console.log('Sending payload to API:', payload);
      await resolveDiscrepancies(job.id, payload);
      console.log('API call completed successfully');
      setSuccess('Changes saved successfully');
      console.log('Save completed, resetting comments');
      
      // Reset comments
      setReviewComments('');
      
      // Reset deleted indices tracking after successful save
      resetDeletedIndices();

      // Clear tracked line item changes after successful save
      clearLineItemChanges();
      
      // Don't update originalResults here - keep showing AI-extracted values
      // The job update will trigger a re-fetch with the saved data
      
      if (onJobUpdate) {
        // Call the parent's refresh function
        onJobUpdate();
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving changes:', err);
      console.error('Error details:', err.response?.data);
      
      // Handle validation errors from server
      if (err.response?.status === 400 && err.response?.data?.message) {
        const errorMessage = err.response.data.message;
        if (errorMessage.includes('Validation failed:')) {
          // Parse field-specific validation errors
          const fieldErrors = {};
          const errorParts = errorMessage.replace('Validation failed: ', '').split('; ');
          
          errorParts.forEach(part => {
            const colonIndex = part.indexOf(': ');
            if (colonIndex > 0) {
              const fieldKey = part.substring(0, colonIndex);
              const errorMsg = part.substring(colonIndex + 2);
              fieldErrors[fieldKey] = errorMsg;
            }
          });
          
          setValidationErrors(fieldErrors);
          setError('Please fix validation errors before saving');
        } else {
          setError(errorMessage);
        }
      } else {
        setError('Failed to save changes');
      }
      
      setTimeout(() => setError(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      
      const exportResponse = await exportExtractionJob(job.id, {
        questionCode,
        errorCode
      });
      
      if (exportResponse.data.success) {
        setSuccess('Export request placed successfully');
        
        // Delay refresh and redirect to show success message
        setTimeout(() => {
          if (onJobUpdate) {
            onJobUpdate();
          }
          // If we're on the details page (showPdfViewer is true), redirect to list
          if (showPdfViewer && window.location.pathname.includes('/extraction-jobs/')) {
            navigate('/extraction-jobs');
          }
        }, 1000);
      } else {
        setError(exportResponse.data.message || 'Export failed');
      }
      setTimeout(() => { setSuccess(''); setError(''); }, 3000);
    } catch (err) {
      console.error('Error exporting job:', err);
      const errorMessage = err.response?.data?.message || 'Failed to export job';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    } finally {
      setExporting(false);
    }
  };


  const handleRetryExtraction = async () => {
    try {
      setSubmitted(true);
      
      const retryResponse = await retryExtractionJob(job.id);
      
      if (retryResponse.data.success) {
        setSuccess('Retry submitted successfully');
        
        // Delay refresh and redirect to show success message
        setTimeout(() => {
          if (onJobUpdate) {
            onJobUpdate();
          }
          // If we're on the details page (showPdfViewer is true), redirect to list
          if (showPdfViewer && window.location.pathname.includes('/extraction-jobs/')) {
            navigate('/extraction-jobs');
          }
        }, 1000);
      } else {
        setError(retryResponse.data.message || 'Retry failed');
      }
      setTimeout(() => { setSuccess(''); setError(''); }, 3000);
    } catch (err) {
      console.error('Error retrying job:', err);
      const errorMessage = err.response?.data?.message || 'Failed to retry';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    } finally {
      setSubmitted(false);
    }
  };

  const handleUpgradeLineItems = async () => {
    try {
      setUpgrading(true);
      setError('');
      
      const response = await upgradeLineItems(job.id);
      
      // On success: update local state with returned data
      if (response.data) {
        // Update editable results with the upgraded data (now has _lineItemId on each item)
        if (response.data.finalResults) {
          setEditableResults({ ...response.data.finalResults });
        }
        
        setSuccess('Line items upgraded successfully. You can now edit them.');
        setTimeout(() => setSuccess(''), 3000);
        
        // Refresh the job data from parent to get lineItemsEditable: true
        if (onJobUpdate) {
          onJobUpdate();
        }
      }
    } catch (err) {
      console.error('Error upgrading line items:', err);
      const errorMessage = err.response?.data?.message || 'Failed to upgrade line items';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    } finally {
      setUpgrading(false);
    }
  };

  const getFieldValue = (fieldKey) => {
    return editableResults[fieldKey];
  };

  const isFieldMissing = (value) => {
    return value === null || value === undefined;
  };

  /**
   * Normalize value to handle empty strings, null, undefined, and N/A consistently.
   * @deprecated Discrepancy detection should be read from the backend response's
   * discrepancyMap (when hasStoredDiscrepancyMap is true) instead of using local
   * normalization. Retained for backward compatibility with legacy jobs that have
   * no stored discrepancy map.
   */
  const normalizeModelValue = (val) => {
    if (val === null || val === undefined) return null;
    const strVal = String(val).trim();
    if (strVal === '' || strVal === 'N/A') return null;
    return strVal;
  };

  const getFieldStatus = (fieldKey, value) => {
    // Check discrepancyMap from the job response for resolution status
    const discrepancyEntry = job?.discrepancyMap?.[fieldKey];
    if (discrepancyEntry && discrepancyEntry.status !== undefined) {
      if (discrepancyEntry.status === RESOLUTION_STATUS.EXACT) {
        return { type: 'ok', label: null, variant: null, tooltip: null };
      }
      if (discrepancyEntry.status === RESOLUTION_STATUS.NORMALIZED || discrepancyEntry.status === RESOLUTION_STATUS.SUBSTRING) {
        const label = discrepancyEntry.status === RESOLUTION_STATUS.NORMALIZED ? 'Normalized' : 'Substring Match';
        return { type: 'resolved', label, variant: 'info', tooltip: discrepancyEntry.resolutionReason || null };
      }
      if (discrepancyEntry.status === RESOLUTION_STATUS.UNRESOLVED) {
        return { type: 'discrepancy', label: 'Discrepancy', variant: 'danger', tooltip: discrepancyEntry.resolutionReason || null };
      }
    }

    // Fallback: legacy jobs without a stored discrepancy map — use local comparison logic
    // If value exists, check if there's a discrepancy
    if (!isFieldMissing(value)) {
      const modelValues = getModelValuesForField(fieldKey);
      if (modelValues && hasDiscrepancy(modelValues)) {
        return { type: 'discrepancy', label: 'Discrepancy', variant: 'danger', tooltip: null };
      }
      return { type: 'ok', label: null, variant: null, tooltip: null };
    }
    
    // Value is missing, check if models found it
    const modelValues = getModelValuesForField(fieldKey);
    if (modelValues) {
      // Check for discrepancy first - if models disagree, it's a discrepancy regardless of final value
      if (hasDiscrepancy(modelValues)) {
        return { type: 'discrepancy', label: 'Discrepancy', variant: 'danger', tooltip: null };
      }
      
      // No discrepancy - check if any models found a value
      const foundCount = [
        normalizeModelValue(modelValues.model1),
        normalizeModelValue(modelValues.model2),
        normalizeModelValue(modelValues.model3)
      ].filter(v => v !== null).length;
      
      if (foundCount === 0) {
        return { type: 'not-found', label: 'Not Found', variant: 'secondary', tooltip: null };
      } else {
        // Models agree there's no value (consensus on empty/null)
        return { type: 'not-found', label: 'Not Found', variant: 'secondary', tooltip: null };
      }
    }
    
    // No comparison data available, default to not found
    return { type: 'not-found', label: 'Not Found', variant: 'secondary', tooltip: null };
  };

  const getLineItemFieldStatus = (lineIndex, fieldKey, value) => {
    // Defensive check: validate alignment before rendering discrepancy indicators.
    // Since soft-deleted items are excluded from the GET response, displayed indices
    // should align with discrepancy map indices. If there's a mismatch (e.g., the
    // discrepancy map references an index beyond the displayed items, or a soft-deleted
    // item leaked into the response), skip discrepancy display for that item.
    const displayedLineItems = editableResults?.LineItems || [];
    const lineItem = displayedLineItems[lineIndex];

    if (lineIndex < 0 || lineIndex >= displayedLineItems.length || !lineItem) {
      // Index out of bounds — skip discrepancy display
      return { type: 'ok', label: null, variant: null, tooltip: null };
    }

    // If a soft-deleted item accidentally appears in the response, skip discrepancy display
    if (lineItem.IsDeleted === true || lineItem.IsDeleted === 'true') {
      return { type: 'ok', label: null, variant: null, tooltip: null };
    }

    // Check discrepancyMap from the job response for resolution status
    const discrepancyKey = `LineItems[${lineIndex}].${fieldKey}`;
    const discrepancyEntry = job?.discrepancyMap?.[discrepancyKey];
    if (discrepancyEntry && discrepancyEntry.status !== undefined) {
      if (discrepancyEntry.status === RESOLUTION_STATUS.EXACT) {
        return { type: 'ok', label: null, variant: null, tooltip: null };
      }
      if (discrepancyEntry.status === RESOLUTION_STATUS.NORMALIZED || discrepancyEntry.status === RESOLUTION_STATUS.SUBSTRING) {
        const label = discrepancyEntry.status === RESOLUTION_STATUS.NORMALIZED ? 'Normalized' : 'Substring Match';
        return { type: 'resolved', label, variant: 'info', tooltip: discrepancyEntry.resolutionReason || null };
      }
      if (discrepancyEntry.status === RESOLUTION_STATUS.UNRESOLVED) {
        return { type: 'discrepancy', label: 'Discrepancy', variant: 'danger', tooltip: discrepancyEntry.resolutionReason || null };
      }
    }

    // Fallback: legacy jobs without a stored discrepancy map — use local comparison logic
    // If value exists, check if there's a discrepancy
    if (!isFieldMissing(value)) {
      const modelValues = getModelValuesForLineItem(lineIndex, fieldKey);
      if (modelValues && hasDiscrepancy(modelValues)) {
        return { type: 'discrepancy', label: 'Discrepancy', variant: 'danger', tooltip: null };
      }
      return { type: 'ok', label: null, variant: null, tooltip: null };
    }
    
    // Value is missing, check if models found it
    const modelValues = getModelValuesForLineItem(lineIndex, fieldKey);
    if (modelValues) {
      // Check for discrepancy first - if models disagree, it's a discrepancy regardless of final value
      if (hasDiscrepancy(modelValues)) {
        return { type: 'discrepancy', label: 'Discrepancy', variant: 'danger', tooltip: null };
      }
      
      // No discrepancy - check if any models found a value
      const foundCount = [
        normalizeModelValue(modelValues.model1),
        normalizeModelValue(modelValues.model2),
        normalizeModelValue(modelValues.model3)
      ].filter(v => v !== null).length;
      
      if (foundCount === 0) {
        return { type: 'not-found', label: 'Not Found', variant: 'secondary', tooltip: null };
      } else {
        // Models agree there's no value (consensus on empty/null)
        return { type: 'not-found', label: 'Not Found', variant: 'secondary', tooltip: null };
      }
    }
    
    // No comparison data available, default to not found
    return { type: 'not-found', label: 'Not Found', variant: 'secondary', tooltip: null };
  };

  /**
   * Determines if model values have a discrepancy (no 2-of-3 consensus).
   * @deprecated Discrepancy detection should be read from the backend response's
   * discrepancyMap (when hasStoredDiscrepancyMap is true) instead of using this
   * local comparison. Retained for backward compatibility with legacy jobs that
   * have no stored discrepancy map.
   */
  const hasDiscrepancy = (modelValues) => {
    if (!modelValues) return false;
    
    const model1 = normalizeModelValue(modelValues.model1);
    const model2 = normalizeModelValue(modelValues.model2);
    const model3 = normalizeModelValue(modelValues.model3);
    
    // Count how many models agree on each value (including null)
    const valueCounts = {};
    [model1, model2, model3].forEach(val => {
      const key = val === null ? '__NULL__' : val;
      valueCounts[key] = (valueCounts[key] || 0) + 1;
    });
    
    // If any value (including null) has 2 or more models agreeing, no discrepancy
    const hasConsensus = Object.values(valueCounts).some(count => count >= 2);
    
    // Discrepancy only if all three values are different (no consensus)
    return !hasConsensus;
  };

  // Helper to extract LineItems JSON substring from raw results JSON and parse
  // with numeric precision preservation (e.g., 65.00 stays as "65.00")
  const parseLineItemsFromRawJson = (rawJson) => {
    if (!rawJson) return null;
    try {
      // Find the LineItems array in the raw JSON string without parsing
      // (parsing would lose numeric precision via JSON.parse)
      const marker = '"LineItems":';
      const startIdx = rawJson.indexOf(marker);
      if (startIdx === -1) return null;
      
      // Find the opening bracket of the array
      const arrayStart = rawJson.indexOf('[', startIdx + marker.length);
      if (arrayStart === -1) return null;
      
      // Find the matching closing bracket
      let depth = 0;
      let arrayEnd = -1;
      for (let i = arrayStart; i < rawJson.length; i++) {
        if (rawJson[i] === '[') depth++;
        else if (rawJson[i] === ']') {
          depth--;
          if (depth === 0) {
            arrayEnd = i;
            break;
          }
        }
      }
      if (arrayEnd === -1) return null;
      
      const lineItemsJson = rawJson.substring(arrayStart, arrayEnd + 1);
      // Convert numeric values to strings to preserve trailing zeroes
      // Convert numeric values to strings to preserve trailing zeroes
      // Validate input length to prevent ReDoS attacks
      if (lineItemsJson.length > 1000000) { // 1MB limit
        throw new Error('JSON string too large');
      }
      const preserved = lineItemsJson.replace(
        /"([^"]+)"\s*:\s*(-?\d+(?:\.\d+)?)\s*(?=[,}\]])/g,
        '"$1":"$2"'
      );
      return JSON.parse(preserved);
    } catch (error) {
      console.error('Error parsing line items from raw JSON:', error);
      return null;
    }
  };

  const getOriginalLineItemValue = (lineIndex, fieldKey) => {
    try {
      // Use raw JSON string to preserve numeric precision (e.g., 65.00 not 65)
      if (job.extractionResultsJson) {
        const lineItems = parseLineItemsFromRawJson(job.extractionResultsJson);
        if (lineItems && lineItems.length > lineIndex) {
          return lineItems[lineIndex]?.[fieldKey] ?? null;
        }
      }
      // Fallback to pre-parsed data
      const extractionLineItems = job.extractionResults?.LineItems;
      if (!extractionLineItems || !Array.isArray(extractionLineItems) || lineIndex >= extractionLineItems.length) {
        return null;
      }
      return extractionLineItems[lineIndex]?.[fieldKey] ?? null;
    } catch {
      return null;
    }
  };

  const getFieldDataType = (fieldKey) => {
    const field = configuredFields.find(f => f.fieldKey === fieldKey);
    return field?.dataType || 'string';
  };

  const normalizeValue = (val) => {
    if (val === null || val === undefined || val === '') return '';
    
    // IRIS-315: Compare as string representations to preserve decimal precision.
    // Previously, Number("0.00") === Number("0") would mask real changes and
    // prevent values like "0.00" from being saved.
    return String(val).trim();
  };

  const getModelValuesForField = (fieldKey) => {
    if (!comparison || !comparison.headerFields) return null;
    
    const fieldComparison = comparison.headerFields[fieldKey] || comparison.detailFields?.[fieldKey];
    if (!fieldComparison) return null;
    
    return {
      model1: fieldComparison.model1,
      model2: fieldComparison.model2,
      model3: fieldComparison.model3
    };
  };

  const getModelValuesForLineItem = (lineIndex, fieldKey) => {
    if (!comparison || !comparison.detailFields || !comparison.detailFields.LineItems) return null;
    
    const lineItems = comparison.detailFields.LineItems;
    
    // Parse line items if they're JSON strings
    const parseLineItems = (items) => {
      if (!items) return [];
      if (Array.isArray(items)) return items;
      if (typeof items === 'string') {
        try {
          // Convert JSON number values to strings to preserve trailing zeroes
          // (e.g., 65.00 stays as "65.00" instead of becoming JS number 65)
          const preserved = items.replace(
            /"([^"]+)"\s*:\s*(-?\d+(?:\.\d+)?)\s*(?=[,}\]])/g,
            '"$1":"$2"'
          );
          const parsed = JSON.parse(preserved);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return [];
        }
      }
      return [];
    };

    const model1Items = parseLineItems(lineItems.model1);
    const model2Items = parseLineItems(lineItems.model2);
    const model3Items = parseLineItems(lineItems.model3);
    
    return {
      model1: model1Items[lineIndex]?.[fieldKey],
      model2: model2Items[lineIndex]?.[fieldKey],
      model3: model3Items[lineIndex]?.[fieldKey]
    };
  };

  // Helper to render a status badge with optional tooltip for resolution reason
  const renderStatusBadge = (fieldStatus, className = '') => {
    if (!fieldStatus.label) return null;
    const badge = <Badge bg={fieldStatus.variant} className={className}>{fieldStatus.label}</Badge>;
    if (fieldStatus.tooltip) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{fieldStatus.tooltip}</Tooltip>}
        >
          {badge}
        </OverlayTrigger>
      );
    }
    return badge;
  };

  const renderExtractedFieldsOnly = () => {
    const extractedFields = Object.keys(editableResults || {}).filter(key => key !== 'LineItems');
    const lineItems = editableResults?.LineItems || [];
    
    return (
      <div>
        {success && <Alert variant="success">{success}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}
        
        <Alert variant="info" className="mb-3">
          No field configuration found. Showing extracted data as-is.
        </Alert>
        
        {/* Header Fields */}
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Extracted Header Fields</h6>
          </Card.Header>
          <Card.Body>
            {extractedFields.length > 0 ? (
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {extractedFields.map(fieldKey => {
                    const value = editableResults[fieldKey];
                    const fieldStatus = getFieldStatus(fieldKey, value);
                    
                    return (
                      <tr key={fieldKey} className={fieldStatus.type === 'resolved' ? 'table-info' : fieldStatus.type !== 'ok' ? 'table-warning' : ''}>
                        <td>
                          {fieldKey}
                          {renderStatusBadge(fieldStatus, 'ms-2')}
                        </td>
                        <td>
                          <EditableField
                            field={{ name: fieldKey, dataType: getFieldDataType(fieldKey) }}
                            value={value}
                            originalValue={originalResults[fieldKey]}
                            onChange={(newValue, hasError) => handleFieldChange(fieldKey, newValue, hasError)}
                            onValidationError={(hasError, errorMessage) => handleFieldValidationError(fieldKey, hasError, errorMessage)}
                            isEditable={isEditableStatus(job.statusId)}
                            modelValues={getModelValuesForField(fieldKey)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <Alert variant="info">No header fields extracted.</Alert>
            )}
          </Card.Body>
        </Card>
        
        {/* Line Items */}
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Extracted Line Items ({lineItems.length})</h6>
          </Card.Header>
          <Card.Body>
            {lineItems.length > 0 ? (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Line #</th>
                      {lineItems[0] && Object.keys(lineItems[0]).map(fieldKey => (
                        <th key={fieldKey}>{fieldKey}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((lineItem, lineIndex) => (
                      <tr key={lineIndex}>
                        <td className="text-center">
                          <Badge bg="primary">{lineIndex + 1}</Badge>
                        </td>
                        {Object.keys(lineItem).map(fieldKey => {
                          const value = lineItem[fieldKey];
                          const fieldStatus = getLineItemFieldStatus(lineIndex, fieldKey, value);
                          
                          return (
                            <td key={fieldKey} className={fieldStatus.type === 'resolved' ? 'table-info' : fieldStatus.type !== 'ok' ? 'table-warning' : ''}>
                              <EditableField
                                field={{ name: fieldKey, dataType: getFieldDataType(fieldKey) }}
                                value={value}
                                originalValue={getOriginalLineItemValue(lineIndex, fieldKey)}
                                onChange={(newValue, hasError) => handleLineItemChange(lineIndex, fieldKey, newValue, hasError)}
                                onValidationError={(hasError, errorMessage) => handleLineItemValidationError(lineIndex, fieldKey, hasError, errorMessage)}
                                isEditable={isEditableStatus(job.statusId)}
                                size="sm"
                                modelValues={getModelValuesForLineItem(lineIndex, fieldKey)}
                              />
                              {fieldStatus.label && (
                                <div>
                                  {renderStatusBadge(fieldStatus, 'small')}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <Alert variant="info">No line items extracted.</Alert>
            )}
          </Card.Body>
        </Card>
        
        {(isEditableStatus(job.statusId)) && (
          <Row>
            <Col className="text-end">
              <Button 
                variant="primary" 
                onClick={handleSaveChanges}
                disabled={saving || Object.keys(validationErrors).length > 0}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              {Object.keys(validationErrors).length > 0 && (
                <div className="text-danger small mt-1">
                  Please fix validation errors before saving
                </div>
              )}
            </Col>
          </Row>
        )}
      </div>
    );
  };

  const renderFieldValue = (field, value, isEditable = false) => {
    const isMissing = isFieldMissing(value);
    
    if (!isEditable) {
      return (
        <span className={isMissing ? 'text-muted fst-italic' : ''}>
          {isMissing ? 'Not extracted' : value?.toString()}
        </span>
      );
    }

    return (
      <Form.Control
        type={field.dataType === 'number' ? 'number' : 'text'}
        value={value || ''}
        onChange={(e) => handleFieldChange(field.fieldKey, e.target.value)}
        className={isMissing ? 'border-warning' : ''}
        placeholder={isMissing ? 'Missing value' : ''}
      />
    );
  };

  // Handle different job statuses
  if (job?.statusId === EXTRACTION_JOB_STATUS.PROCESSING) {
    return (
      <Row>
        <Col md={showPdfViewer ? 6 : 12}>
          <div className="text-center my-4">
            <Spinner animation="border" size="sm" className="me-2" />
            Processing invoice data...
          </div>
        </Col>
        {showPdfViewer && (
          <Col md={6}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">Invoice Document</h6>
              </Card.Header>
              <Card.Body>
                {loadingPdf ? (
                  <div className="text-center my-5">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading PDF...</span>
                    </Spinner>
                    <p className="mt-2">Loading invoice document...</p>
                  </div>
                ) : pdfUrl ? (
                  <iframe
                    src={pdfUrl}
                    title="Invoice PDF"
                    width="100%"
                    height="600px"
                  />
                ) : (
                  <Alert variant="info">Invoice document not available for preview.</Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    );
  }

  const canRetryExtraction = job?.statusId === EXTRACTION_JOB_STATUS.FAILED || job?.statusId === EXTRACTION_JOB_STATUS.UNPROCESSABLE;

  if (job?.statusId === EXTRACTION_JOB_STATUS.FAILED) {
    return (
      <Row>
        <Col md={showPdfViewer ? 6 : 12}>

          <Alert variant="danger">
            <p><strong>Error:</strong> {job.errorMessage || 'Unknown error occurred during processing'}</p>
          </Alert>
        </Col>
        {showPdfViewer && (
          <Col md={6}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">Invoice Document</h6>
              </Card.Header>
              <Card.Body>
                {loadingPdf ? (
                  <div className="text-center my-5">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading PDF...</span>
                    </Spinner>
                    <p className="mt-2">Loading invoice document...</p>
                  </div>
                ) : pdfUrl ? (
                  <iframe
                    src={pdfUrl}
                    title="Invoice PDF"
                    width="100%"
                    height="600px"
                  />
                ) : (
                  <Alert variant="info">Invoice document not available for preview.</Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    );
  }

  if (job?.statusId === EXTRACTION_JOB_STATUS.REQUESTED) {
    return (
      <Row>
        <Col md={showPdfViewer ? 6 : 12}>
          <Alert variant="info">
            Waiting for extraction to complete.
          </Alert>
        </Col>
        {showPdfViewer && (
          <Col md={6}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">Invoice Document</h6>
              </Card.Header>
              <Card.Body>
                {loadingPdf ? (
                  <div className="text-center my-5">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading PDF...</span>
                    </Spinner>
                    <p className="mt-2">Loading invoice document...</p>
                  </div>
                ) : pdfUrl ? (
                  <iframe
                    src={pdfUrl}
                    title="Invoice PDF"
                    width="100%"
                    height="600px"
                  />
                ) : (
                  <Alert variant="info">Invoice document not available for preview.</Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    );
  }

  if (loading) {
    return (
      <Card>
        <Card.Body>
          <div className="text-center">Loading field configuration...</div>
        </Card.Body>
      </Card>
    );
  }



  // Check if we have a ruleset type ID
  const rulesetTypeId = job?.extractionRuleId;
  
  // If no configuration is available, show extracted fields directly
  if (!rulesetTypeId && configuredFields.length === 0) {
    return renderExtractedFieldsOnly();
  }

  const headerFields = configuredFields.filter(field => field.fieldType === 0 || field.fieldType === 'Header');
  // detailFields is now computed earlier in the component for the hook
  const lineItems = editableResults.LineItems || [];
  const maxLineItems = Math.max(lineItems.length, 1);

  const canExport = job.statusId === EXTRACTION_JOB_STATUS.READY_FOR_EXPORT;

  return (
    <div>
      {/* Export Button - Top Right Corner */}
      <div className="d-flex justify-content-end mb-3">
        {canExport && (
          <Button 
            variant="primary" 
            size="sm"
            onClick={handleExport}
            disabled={exporting || job.statusId === EXTRACTION_JOB_STATUS.EXPORTED}
          >
            {exporting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Exporting...
              </>
            ) : job.statusId === EXTRACTION_JOB_STATUS.EXPORTED ? (
              'Already Exported'
            ) : (
              'Export Job'
            )}
          </Button>
        )}
      </div>
      
      
      <Row>
        <Col md={6}>
          <div style={{ height: '660px', overflowY: 'auto' }}>
          {success && <Alert variant="success">{success}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          
          {/* IRIS-360: Augmentation re-trigger warning */}
          {job?.statusId === EXTRACTION_JOB_STATUS.READY_FOR_EXPORT && job?.hasCompletedAugmentation === true && (
            <Alert variant="warning" className="mb-3">
              <Alert.Heading className="h6 mb-1">Augmentation will re-run</Alert.Heading>
              <small>This invoice has been augmented. Saving field changes will re-trigger augmentation processing, and the job will return to &quot;Augmenting&quot; status until complete.</small>
            </Alert>
          )}

          {/* Header Fields Section */}
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">Header Fields</h6>
            </Card.Header>
            <Card.Body>
              {headerFields.length > 0 ? (
                <div className="row g-2">
                  {headerFields.map(field => {
                    const value = getFieldValue(field.fieldKey);
                    const fieldStatus = getFieldStatus(field.fieldKey, value);
                    const fieldRef = React.createRef();
                    
                    const handleFieldClick = () => {
                      // Focus the input when clicking anywhere in the field container
                      const input = fieldRef.current?.querySelector('input, select');
                      if (input) {
                        input.click();
                        input.focus();
                      }
                    };
                    
                    return (
                      <div key={field.id} className="col-md-6">
                        <div 
                          ref={fieldRef}
                          className={`border rounded ${isEditableStatus(job.statusId) ? 'p-2' : 'p-1'} ${fieldStatus.type === 'resolved' ? 'border-info bg-info bg-opacity-10' : fieldStatus.type !== 'ok' ? 'border-warning bg-warning bg-opacity-10' : ''}`}
                          style={{ cursor: isEditableStatus(job.statusId) ? 'pointer' : 'default' }}
                          onClick={handleFieldClick}
                        >
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Extracted value: {job.extractionResults?.[field.fieldKey] || 'Not extracted'}</Tooltip>}
                          >
                            <label className={`form-label small fw-bold ${isEditableStatus(job.statusId) ? 'mb-1' : 'mb-0'}`}>
                              {field.displayName || field.name}
                              {renderStatusBadge(fieldStatus, 'ms-1 small')}

                            </label>
                          </OverlayTrigger>
                          <EditableField
                            field={field}
                            value={value}
                            originalValue={originalResults[field.fieldKey]}
                            onChange={(newValue, hasError) => handleFieldChange(field.fieldKey, newValue, hasError)}
                            onValidationError={(hasError, errorMessage) => handleFieldValidationError(field.fieldKey, hasError, errorMessage)}
                            isEditable={isEditableStatus(job.statusId)}
                            size="sm"
                            modelValues={getModelValuesForField(field.fieldKey)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Alert variant="info">No header fields configured for this customer.</Alert>
              )}
            </Card.Body>
          </Card>
          {detailFields.length > 0 && (
            
              <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Line Items ({lineItems.length})</h6>
                  <div className="d-flex align-items-center gap-2">
                    {lineItems.length > itemsPerPage && (
                      <small className="text-muted">
                        Showing {Math.min((currentPage - 1) * itemsPerPage + 1, lineItems.length)}-{Math.min(currentPage * itemsPerPage, lineItems.length)} of {lineItems.length}
                      </small>
                    )}
                    {job.lineItemsEditable === false && isEditableStatus(job.statusId) && (
                      <Badge bg="secondary" className="ms-2">Read-Only</Badge>
                    )}
                  </div>
                </Card.Header>
                <Card.Body>
                  {lineItems.length > 0 ? (
                    <>
                      <div className="table-responsive">
                        <Table striped bordered hover size="sm" className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{width: '80px'}} className="text-center small">#</th>
                              {detailFields.map(field => (
                                <th key={field.id} className="small">{field.displayName || field.name}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const startIndex = (currentPage - 1) * itemsPerPage;
                              const endIndex = Math.min(startIndex + itemsPerPage, lineItems.length);
                              const paginatedItems = lineItems.slice(startIndex, endIndex);
                              
                              return paginatedItems.map((lineItem, paginatedIndex) => {
                                const actualIndex = startIndex + paginatedIndex;
                                const hasAnyValue = detailFields.some(field => 
                                  !isFieldMissing(lineItem[field.fieldKey])
                                );
                                
                                return (
                                  <tr key={actualIndex} className={hasAnyValue ? '' : 'table-secondary'}>
                                    <td className="text-center small fw-bold">
                                      {(isEditableStatus(job.statusId) && job.lineItemsEditable !== false) && (
                                        <Button
                                          variant="link"
                                          size="sm"
                                          className="text-danger p-0 me-1"
                                          onClick={() => handleRemoveLineItem(actualIndex)}
                                          title="Remove line item"
                                        >
                                          <i className="bi bi-x-circle"></i>
                                        </Button>
                                      )}
                                      {actualIndex + 1}
                                    </td>
                                    {detailFields.map(field => {
                                      const value = lineItem[field.fieldKey];
                                      const fieldStatus = getLineItemFieldStatus(actualIndex, field.fieldKey, value);
                                      
                                      return (
                                        <td key={field.id} className={fieldStatus.type === 'resolved' ? 'table-info' : fieldStatus.type !== 'ok' ? 'table-warning' : ''}>
                                          <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>Extracted value: {getOriginalLineItemValue(actualIndex, field.fieldKey) || 'Not extracted'}</Tooltip>}
                                          >
                                            <div>
                                              <EditableField
                                                field={field}
                                                value={value}
                                                originalValue={getOriginalLineItemValue(actualIndex, field.fieldKey)}
                                                onChange={(newValue, hasError) => handleLineItemChange(actualIndex, field.fieldKey, newValue, hasError)}
                                                onValidationError={(hasError, errorMessage) => handleLineItemValidationError(actualIndex, field.fieldKey, hasError, errorMessage)}
                                                isEditable={isEditableStatus(job.statusId) && job.lineItemsEditable !== false}
                                                size="sm"
                                                modelValues={getModelValuesForLineItem(actualIndex, field.fieldKey)}
                                              />
                                              {fieldStatus.label && (
                                                <div className="mt-1">
                                                  {renderStatusBadge(fieldStatus, 'small')}
                                                </div>
                                              )}
                                            </div>
                                          </OverlayTrigger>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              });
                            })()
                            }
                          </tbody>
                        </Table>
                      </div>
                      
                      {/* Pagination */}
                      {lineItems.length > itemsPerPage && (
                        <div className="d-flex justify-content-center mt-3">
                          <nav>
                            <ul className="pagination pagination-sm">
                              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button 
                                  className="page-link" 
                                  onClick={() => setCurrentPage(currentPage - 1)}
                                  disabled={currentPage === 1}
                                >
                                  Previous
                                </button>
                              </li>
                              {Array.from({ length: Math.ceil(lineItems.length / itemsPerPage) }, (_, i) => (
                                <li key={i + 1} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => setCurrentPage(i + 1)}
                                  >
                                    {i + 1}
                                  </button>
                                </li>
                              ))}
                              <li className={`page-item ${currentPage === Math.ceil(lineItems.length / itemsPerPage) ? 'disabled' : ''}`}>
                                <button 
                                  className="page-link" 
                                  onClick={() => setCurrentPage(currentPage + 1)}
                                  disabled={currentPage === Math.ceil(lineItems.length / itemsPerPage)}
                                >
                                  Next
                                </button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm" className="mb-0">
                        <thead className="table-light">
                          <tr>
                            {(isEditableStatus(job.statusId) && job.lineItemsEditable !== false) && (
                              <th style={{width: '50px'}} className="text-center"></th>
                            )}
                            <th style={{width: '80px'}} className="text-center small">#</th>
                            {detailFields.map(field => (
                              <th key={field.id} className="small">{field.displayName || field.name}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td colSpan={detailFields.length + ((isEditableStatus(job.statusId) && job.lineItemsEditable !== false) ? 2 : 1)} className="text-center text-muted py-3">
                              No line items extracted.
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  )}
                  
                  {/* Add Line Item Button - hidden for legacy jobs */}
                  {(isEditableStatus(job.statusId) && job.lineItemsEditable !== false) && (
                    <div className="mt-2">
                      <Button 
                        variant="success" 
                        size="sm"
                        onClick={handleAddLineItem}
                      >
                        <i className="bi bi-plus-circle me-1"></i>
                        Add Line Item
                      </Button>
                    </div>
                  )}
                  
                  {/* Edit Line Items Button - shown only for legacy jobs */}
                  {(isEditableStatus(job.statusId) && job.lineItemsEditable === false) && (
                    <div className="mt-2">
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        id="edit-line-items-btn"
                        onClick={handleUpgradeLineItems}
                        disabled={upgrading}
                      >
                        {upgrading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-1"
                            />
                            Upgrading...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-pencil-square me-1"></i>
                            Edit Line Items
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            
          )}
          {/* Save Button for Editable Mode */}
          {(isEditableStatus(job.statusId)) && (
            <Row>
              <Col className="text-end">
                <Button 
                  variant="primary" 
                  onClick={handleSaveChanges}
                  disabled={saving || Object.keys(validationErrors).length > 0}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                {Object.keys(validationErrors).length > 0 && (
                  <div className="text-danger small mt-1">
                    Please fix validation errors before saving
                  </div>
                )}
              </Col>
            </Row>
          )}
        </div>
      </Col>
      
      {/* PDF Viewer */}
      {showPdfViewer && (
        <Col md={6}>
          <Card>
              <Card.Header>
                <h6 className="mb-0">Invoice Document</h6>
              </Card.Header>
            <Card.Body>
              <div className="pdf-viewer">
                  {loadingPdf ? (
                    <div className="text-center my-5">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading PDF...</span>
                      </Spinner>
                      <p className="mt-2">Loading invoice document...</p>
                    </div>
                  ) : pdfUrl ? (
                    <iframe
                      src={pdfUrl}
                      title="Invoice PDF"
                      width="100%"
                      height="600px"
                    />
                  ) : (
                    <div className="text-center my-5">
                      <Alert variant="info">
                        Invoice document not available for preview.
                      </Alert>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
        </Col>
      )}
      </Row>
      
      {/* Review Comments Modal */}
      <Modal show={showCommentsModal} onHide={() => {
        setShowCommentsModal(false);
        setReviewComments(job?.reviewComments || '');
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Add Review Comments</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Comments (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={reviewComments}
              onChange={(e) => {
                const value = e.target.value;
                if (validateInput(value)) {
                  setReviewComments(value);
                }
              }}
              placeholder="Explain why changes were necessary..."
              maxLength={2000}
            />
            <Form.Text className="text-muted">
              {reviewComments.length}/2000 characters
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowCommentsModal(false);
            setReviewComments(job?.reviewComments || '');
          }}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={() => performSave(reviewComments)}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ExtractionDetailsView;